/**
 * Shared shaping for a season breakdown — the per-event result rows the
 * contestant's own page and the association's contestant detail both render.
 *
 * Both surfaces read the SAME rows off the same service, so the folding and
 * the scope label live here rather than being written twice and drifting.
 *
 * External rows (`row.external`) are delegate-entered results from rodeos
 * HighCall did not run. They carry no `rodeo_id`, so they fold under the
 * rodeo name + date the delegate typed, and every surface highlights them —
 * a tinted row and an "External" badge — so a reader can tell a scored result
 * from a reported one at a glance.
 */

import type { SeasonBreakdownRow } from '@/api/extensions/useSeasonWinningsOps';

/** Which payout this row came from — a go-round, the event's average, or outside HighCall. */
export function scopeLabel(row: SeasonBreakdownRow): string {
  if (row.external) return 'External';
  return row.scope === 'round' && row.round_number
    ? `Round ${row.round_number}`
    : 'Average';
}

/**
 * A stable key for a breakdown row. A contestant can hold two entries in one
 * event (a header and a heeler slot), so managed rows key on scope + placement
 * too; an external row is its own record and keys on that.
 */
export function breakdownRowKey(row: SeasonBreakdownRow): string {
  if (row.external_result_id) return `external-${row.external_result_id}`;
  return `${row.event_id}-${row.scope}-${row.round_number ?? 0}-${row.placement ?? 0}`;
}

/** Tailwind classes that tint an external row on any breakdown table. */
export const EXTERNAL_ROW_CLASS = 'bg-amber-500/10 hover:bg-amber-500/15';

export interface RodeoGroup {
  /** `rodeos.id` for a managed rodeo; a name+date key for an external one. */
  rodeo_id: string;
  rodeo_name: string;
  start_date: string | null;
  /** True when every row under it is an external (outside HighCall) result. */
  external: boolean;
  rows: SeasonBreakdownRow[];
}

/**
 * Breakdown rows folded into their rodeos, most recent rodeo first. External
 * rows group by the rodeo name and date the delegate entered, so two outside
 * results from the same rodeo sit under one header.
 */
export function groupByRodeo(breakdown: SeasonBreakdownRow[]): RodeoGroup[] {
  const groups = new Map<string, RodeoGroup>();
  for (const row of breakdown) {
    const key = row.rodeo_id ?? `external:${row.rodeo_name ?? ''}|${row.start_date ?? ''}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        rodeo_id: key,
        rodeo_name: row.rodeo_name ?? 'Rodeo',
        start_date: row.start_date,
        external: row.external,
        rows: [],
      };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  return [...groups.values()].sort((a, b) =>
    (b.start_date ?? '').localeCompare(a.start_date ?? ''),
  );
}
